/**
 * We use simple fetch calls to call this API, as the result is a global singleton
 * and we don't need the complexity of using the store to manage it.
 */

import type SessionService from '@efdigital/ll-addon-auth/services/session';
import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import { addCurrentAuthHeader } from '@efdigital/ll-addon-auth/utils/auth-header';
import type { MediaImage } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import config from '@efdigital/ll-admin-v2/config/environment';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api';
import type { SizePreset } from '@efdigital/ll-admin-v2/lib/media/sizes';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import {
  createRecord,
  deleteRecord,
  findRecord,
  serializePatch,
  updateRecord,
} from '@warp-drive/utilities/json-api';

const apis: Apis = config['ll-addon-auth-apis'];

export interface SignedUrlResponse {
  url: string;
}

export function getMediaImage(id: string) {
  return findRecord<MediaImage>(DataTypeContent.MediaImage, id, {
    ...jsonApiOptions,
  });
}

export function createMediaImage(mediaImage: MediaImage, store: Store) {
  const init = createRecord(mediaImage, {
    ...jsonApiOptions,
  });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(mediaImage)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function updateMediaImage(mediaImage: MediaImage, store: Store) {
  const init = updateRecord<MediaImage>(mediaImage, {
    ...jsonApiOptions,
    patch: true,
  });

  init.body = JSON.stringify(
    serializePatch(store.cache, cacheKeyFor(mediaImage))
  );

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function deleteMediaImage(image: MediaImage) {
  const init = deleteRecord(image, {
    ...jsonApiOptions,
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export async function buildGetSignedUploadUrlRequest(
  imageId: string,
  contentType: string,
  session: SessionService
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contentType: contentType,
      }),
    },
    { session }
  );

  return new Request(
    `${apis.systemsTransitionalApi.host}/management-api/media/v1/mediaImages/${encodeURIComponent(imageId)}`,
    init
  );
}

export async function parseGetSignedUploadUrlResponse(
  response: Response
): Promise<SignedUrlResponse> {
  if (!response.ok) {
    throw new Error(`Failed to get signed upload URL: ${response.statusText}`);
  }
  return (await response.json()) as SignedUrlResponse;
}

export async function buildGetAiTagsRequest(
  imageUrl: string,
  session: SessionService
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageUrl }),
    },
    { session }
  );

  return new Request(
    `${apis.systemsTransitionalApi.host}/ai-admin-assistant/v1/ai/tag-image`,
    init
  );
}

export type GetAiTagsResponse = {
  tags?: string[];
};

export async function parseGetAiTagsResponse(
  response: Response
): Promise<GetAiTagsResponse> {
  if (!response.ok) {
    throw new Error(`Failed to get AI tags: ${response.statusText}`);
  }
  return (await response.json()) as GetAiTagsResponse;
}

export async function buildEnsureImageSizeRequest(
  image: MediaImage,
  requiredSize: {
    size: string;
    customSize?: SizePreset;
  },
  session: SessionService
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        size: requiredSize.size,
        customSize: requiredSize.customSize,
      }),
    },
    { session }
  );

  return new Request(
    `${apis.systemsTransitionalApi.host}/management-api/media/v1/mediaImages/${encodeURIComponent(image.id!)}/size`,
    init
  );
}
